// src/components/Header/ProfileDropdown.tsx
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { logout } from '../../store/authSlice';
import { User, Settings, LogOut, Bell, HelpCircle } from 'lucide-react';
import { LogoutModal } from '../LogoutModal/LogoutModal';
import './Modals.scss';

interface ProfileDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ isOpen, onClose, anchorEl }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (isOpen && anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen, anchorEl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          anchorEl && !anchorEl.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorEl]);

  if (!isOpen) return null;

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      await dispatch(logout()).unwrap();
      setShowLogoutModal(false);
      onClose();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    onClose();
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    onClose();
  };

  // Get user initials (first 2 characters of name)
  const getUserInitials = () => {
    if (!user?.name) return 'US';
    const names = user.name.trim().split(' ');
    if (names.length >= 2) {
      // First letter of first name + first letter of last name
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    // Just take first 2 characters of single name
    return user.name.substring(0, 2).toUpperCase();
  };

  return (
    <div ref={menuRef} className="profile-dropdown" style={{ top: `${position.top}px`, right: `${position.right}px` }}>
      <div className="profile-dropdown-header">
        <div className="profile-avatar-large">
          {user?.avatar ? (
            <img src={user.avatar} alt={user.name || 'Profile'} />
          ) : (
            <span className="profile-avatar-initials">{getUserInitials()}</span>
          )}
        </div>
        <div className="profile-info">
          <h4>{user?.name || user?.userName || 'User'}</h4>
          <p>{user?.email || 'user@example.com'}</p>
        </div>
      </div>

      <div className="profile-dropdown-divider"></div>

      <div className="profile-dropdown-menu">
        <button className="profile-menu-item" onClick={handleProfileClick}>
          <User size={18} />
          <span>My Profile</span>
        </button>
        <button className="profile-menu-item" onClick={handleSettingsClick}>
          <Settings size={18} />
          <span>Settings</span>
        </button>
        <button className="profile-menu-item">
          <Bell size={18} />
          <span>Notifications</span>
        </button>
        <button className="profile-menu-item">
          <HelpCircle size={18} />
          <span>Help & Support</span>
        </button>
      </div>

      <div className="profile-dropdown-divider"></div>

      <button className="profile-menu-item logout-item" onClick={handleLogoutClick}>
        <LogOut size={18} />
        <span>Logout</span>
      </button>

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        loading={isLoggingOut}
      />
    </div>
  );
};
